/* ════════════════════════════════════════════════════════════════════════
   pw-storefront.js  —  Shopify Storefront API client
   ────────────────────────────────────────────────────────────────────────
   Live product search/fetch via Shopify's public Storefront API.
   Replaces the catalogue.json snapshot pattern with always-live data.

   Exposes window.pwStorefront with:
     init(domain, token, version)  — must be called once before queries
     searchProducts(query, opts)   — search by query string, returns ranked list
     listProducts(opts)            — paginated browse without query
     getProduct(handle)            — full product detail incl. metafields, variants
     listFilters()                 — type & vendor lists for dropdown filters
     resolveSiblings(handle)       — grouped colourways for a style

   Architecture:
     - Pure ES5 + fetch + Promises. No deps. Browser-only.
     - Per-session in-memory cache keyed by query/handle/cursor.
     - All product objects normalised into the same shape the rest of the
       Quote Builder expects (i, t, v, y, img, cw, ci, sz, h, em, ap, hg,
       ap_p, hg_p, do, moq, fw, vm). This means pw-pricing-engine and the
       front-end render code don't change.

   Reference: https://shopify.dev/docs/api/storefront
   ════════════════════════════════════════════════════════════════════════ */

(function (root) {
  'use strict';

  /* ── Config + state ──────────────────────────────────────────────────── */
  var CONFIG = {
    domain:  '',                /* e.g. "printwear1.myshopify.com" */
    token:   '',                /* Storefront API public token */
    version: '2025-10',         /* Storefront API version */
  };

  /* Session caches — Map keyed by cache key. Cleared on page reload. */
  var ProductCache    = {};   /* handle → normalised product */
  var SearchCache     = {};   /* query string → array */
  var FiltersCache    = null; /* { types, vendors } */
  var SiblingsCache   = {};   /* siblings_collection_id → array of products */


  /* ── Init ────────────────────────────────────────────────────────────── */
  function init(domain, token, version) {
    if (!domain || !token) {
      throw new Error('[pw-storefront] init: domain and token required');
    }
    CONFIG.domain  = String(domain).replace(/^https?:\/\//, '').replace(/\/+$/, '');
    CONFIG.token   = token;
    if (version) CONFIG.version = version;
  }


  /* ── Low-level GraphQL fetch ─────────────────────────────────────────── */
  function gql(query, variables) {
    if (!CONFIG.domain || !CONFIG.token) {
      return Promise.reject(new Error('Storefront client not initialised'));
    }
    var url = 'https://' + CONFIG.domain + '/api/' + CONFIG.version + '/graphql.json';
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Shopify-Storefront-Access-Token': CONFIG.token
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    })
    .then(function (r) {
      if (!r.ok) throw new Error('Storefront HTTP ' + r.status);
      return r.json();
    })
    .then(function (res) {
      if (res.errors && res.errors.length) {
        console.warn('[pw-storefront] GraphQL errors', res.errors);
        throw new Error(res.errors[0].message || 'GraphQL error');
      }
      return res.data;
    });
  }


  /* ════════════════════════════════════════════════════════════════════
     QUERY FRAGMENTS
     ════════════════════════════════════════════════════════════════════ */

  /* Compact product fragment for search/browse listings — minimises payload.
     Excludes variants and full metafield list. Detail fetch fills the gap. */
  var FRAGMENT_PRODUCT_LISTING = ''
    + 'fragment ProductListing on Product {'
    +   ' id'
    +   ' handle'
    +   ' title'
    +   ' vendor'
    +   ' productType'
    +   ' availableForSale'
    +   ' featuredImage { url altText }'
    +   ' siblingsRef: metafield(namespace: "cornerstone", key: "siblings_collection") { value }'
    +   ' colourMf: metafield(namespace: "custom", key: "colour") { value }'
    + '}';

  /* Full product fragment with metafields and variants — for product detail. */
  var FRAGMENT_PRODUCT_DETAIL = ''
    + 'fragment ProductDetail on Product {'
    +   ' id'
    +   ' handle'
    +   ' title'
    +   ' vendor'
    +   ' productType'
    +   ' availableForSale'
    +   ' featuredImage { url altText }'
    +   ' images(first: 20) { edges { node { url altText } } }'
    +   ' options { name values }'
    +   ' variants(first: 50) {'
    +     ' edges { node {'
    +       ' id'
    +       ' title'
    +       ' availableForSale'
    +       ' selectedOptions { name value }'
    +       ' price { amount currencyCode }'
    +       ' image { url }'
    +     ' } }'
    +   ' }'
    +   ' siblingsRef: metafield(namespace: "cornerstone", key: "siblings_collection") { value reference { ... on Collection { id handle } } }'
    +   ' styleCodeMf: metafield(namespace: "custom", key: "style_code") { value }'
    +   ' embellishmentMf: metafield(namespace: "custom", key: "embellishment") { value }'
    +   ' colourMf: metafield(namespace: "custom", key: "colour") { value }'
    +   ' shortTitleMf: metafield(namespace: "custom", key: "short_title") { value }'
    +   ' moqMf: metafield(namespace: "custom", key: "moq") { value }'
    +   ' priceTiersMf: metafield(namespace: "custom", key: "price_tiers") { value }'
    +   ' decoOptionsMf: metafield(namespace: "custom", key: "decoration_options") { value }'
    +   ' availableSizesMf: metafield(namespace: "custom", key: "available_sizes") { value }'
    +   ' fabricWeightMf: metafield(namespace: "custom", key: "fabric_weight-gsm") { value }'
    +   ' volumeMlMf: metafield(namespace: "custom", key: "volume_ml") { value }'
    + '}';


  /* ════════════════════════════════════════════════════════════════════
     SEARCH
     ════════════════════════════════════════════════════════════════════ */

  /**
   * Search products. Translates user-typed query into Shopify search syntax,
   * adds availability filter, returns normalised listings.
   *
   * @param {string} q     User search string ("staple tee", "5001", etc.)
   * @param {object} opts  { first?: number = 50, type?: string, vendor?: string }
   * @returns {Promise<Array>} normalised product listings
   */
  function searchProducts(q, opts) {
    opts = opts || {};
    var first = opts.first || 50;
    q = String(q || '').trim();

    /* Build Shopify search query. Available_for_sale ensures Drafts/archived
       are auto-excluded. Type/vendor act as filters narrowing the results. */
    var searchTerms = [];
    if (q) {
      /* Split user query into tokens and AND them together — so "as colour
         staple" matches a product whose title or vendor contains all three. */
      q.split(/\s+/).filter(Boolean).forEach(function (token) {
        searchTerms.push(JSON.stringify(token));
      });
    }
    searchTerms.push('available_for_sale:true');
    if (opts.type)   searchTerms.push('product_type:' + JSON.stringify(opts.type));
    if (opts.vendor) searchTerms.push('vendor:'       + JSON.stringify(opts.vendor));

    var queryStr = searchTerms.join(' AND ');
    var cacheKey = 'search:' + queryStr + ':' + first;
    if (SearchCache[cacheKey]) return Promise.resolve(SearchCache[cacheKey]);

    var query = ''
      + FRAGMENT_PRODUCT_LISTING
      + ' query Search($q: String!, $first: Int!) {'
      +   ' products(first: $first, query: $q) {'
      +     ' edges { node { ...ProductListing } }'
      +   ' }'
      + ' }';

    return gql(query, { q: queryStr, first: first })
      .then(function (data) {
        var results = (data.products.edges || []).map(function (e) {
          return normaliseListing(e.node);
        });
        SearchCache[cacheKey] = results;
        return results;
      });
  }


  /* ════════════════════════════════════════════════════════════════════
     LIST / BROWSE  (cursor-based pagination)
     ════════════════════════════════════════════════════════════════════ */

  /**
   * Browse products with cursor pagination — used by the Browse modal.
   * @param {object} opts  { first?: 60, after?: cursor, type?, vendor? }
   * @returns {Promise<{ items: Array, hasNextPage: bool, endCursor: string|null }>}
   */
  function listProducts(opts) {
    opts = opts || {};
    var first = opts.first || 60;
    var searchTerms = ['available_for_sale:true'];
    if (opts.type)   searchTerms.push('product_type:' + JSON.stringify(opts.type));
    if (opts.vendor) searchTerms.push('vendor:'       + JSON.stringify(opts.vendor));
    var queryStr = searchTerms.join(' AND ');

    var query = ''
      + FRAGMENT_PRODUCT_LISTING
      + ' query Browse($q: String!, $first: Int!, $after: String) {'
      +   ' products(first: $first, after: $after, query: $q, sortKey: TITLE) {'
      +     ' edges { cursor node { ...ProductListing } }'
      +     ' pageInfo { hasNextPage endCursor }'
      +   ' }'
      + ' }';

    return gql(query, { q: queryStr, first: first, after: opts.after || null })
      .then(function (data) {
        return {
          items:       (data.products.edges || []).map(function (e) { return normaliseListing(e.node); }),
          hasNextPage: data.products.pageInfo.hasNextPage,
          endCursor:   data.products.pageInfo.endCursor
        };
      });
  }


  /* ════════════════════════════════════════════════════════════════════
     PRODUCT DETAIL  (full data including variants + metafields)
     ════════════════════════════════════════════════════════════════════ */

  function getProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (ProductCache[handle]) return Promise.resolve(ProductCache[handle]);

    var query = ''
      + FRAGMENT_PRODUCT_DETAIL
      + ' query Detail($handle: String!) { productByHandle(handle: $handle) { ...ProductDetail } }';

    return gql(query, { handle: handle })
      .then(function (data) {
        if (!data || !data.productByHandle) return null;
        var p = normaliseDetail(data.productByHandle);
        ProductCache[handle] = p;
        return p;
      });
  }


  /* ════════════════════════════════════════════════════════════════════
     SIBLINGS  (resolve a "style group" → all colourways)
     ════════════════════════════════════════════════════════════════════
     Each apparel SKU has a metafield cornerstone.siblings_collection that
     references a Shopify collection containing all colourways of the same
     base style. We fetch that collection once and merge the colourways
     into a single Quote Builder line. */
  function resolveSiblings(handle) {
    if (!handle) return Promise.resolve([]);

    return getProduct(handle).then(function (p) {
      if (!p || !p._siblingsCollectionId) return [p].filter(Boolean);

      var cacheKey = p._siblingsCollectionId;
      if (SiblingsCache[cacheKey]) return SiblingsCache[cacheKey];

      var query = ''
        + FRAGMENT_PRODUCT_DETAIL
        + ' query Siblings($id: ID!) {'
        +   ' collection(id: $id) {'
        +     ' products(first: 100) { edges { node { ...ProductDetail } } }'
        +   ' }'
        + ' }';

      return gql(query, { id: cacheKey }).then(function (data) {
        var prods = ((data.collection && data.collection.products.edges) || [])
          .map(function (e) { return normaliseDetail(e.node); })
          .filter(function (x) { return x.availableForSale; });
        SiblingsCache[cacheKey] = prods;
        prods.forEach(function (x) { ProductCache[x.h] = x; });
        return prods;
      });
    });
  }


  /* ════════════════════════════════════════════════════════════════════
     FILTERS  (Type + Vendor lists for the dropdown filters)
     ════════════════════════════════════════════════════════════════════
     Storefront API doesn't expose a "list all distinct types/vendors"
     query directly. Cheapest option: fetch a few hundred products,
     dedupe types and vendors. Cache once per session. */
  function listFilters() {
    if (FiltersCache) return Promise.resolve(FiltersCache);

    var query = ''
      + ' query Filters {'
      +   ' products(first: 250, query: "available_for_sale:true") {'
      +     ' edges { node { productType vendor } }'
      +   ' }'
      + ' }';

    return gql(query, {}).then(function (data) {
      var types = {}, vendors = {};
      (data.products.edges || []).forEach(function (e) {
        var t = (e.node.productType || '').trim();
        var v = (e.node.vendor || '').trim();
        if (t) types[t]     = (types[t]   || 0) + 1;
        if (v) vendors[v]   = (vendors[v] || 0) + 1;
      });
      FiltersCache = {
        types:   Object.keys(types).map(function (k)   { return { name: k, count: types[k]   }; }),
        vendors: Object.keys(vendors).map(function (k) { return { name: k, count: vendors[k] }; })
      };
      FiltersCache.types.sort(function (a, b)   { return a.name.toLowerCase().localeCompare(b.name.toLowerCase()); });
      FiltersCache.vendors.sort(function (a, b) { return a.name.toLowerCase().localeCompare(b.name.toLowerCase()); });
      return FiltersCache;
    });
  }


  /* ════════════════════════════════════════════════════════════════════
     NORMALISATION  (Storefront API → Quote Builder shape)
     ════════════════════════════════════════════════════════════════════
     Output schema matches catalogue.json so existing render code works.
     Short keys: i, t, v, y, img, cw, ci, sz, h, em, ap, hg, ap_p, hg_p,
                 do, moq, fw, vm. */

  function safeJson(s) {
    if (!s) return null;
    try { return JSON.parse(s); } catch (e) { return null; }
  }
  function safeInt(s) {
    var n = parseInt(s, 10);
    return isNaN(n) ? 0 : n;
  }

  /* Listing: minimal data, used for search-results and browse-card rows. */
  function normaliseListing(p) {
    return {
      i:   p.id,
      t:   p.title,
      v:   p.vendor,
      y:   p.productType,
      img: (p.featuredImage && p.featuredImage.url) || '',
      h:   p.handle,
      _siblingsCollectionId: (p.siblingsRef && p.siblingsRef.value) || null,
      _colour: (p.colourMf && p.colourMf.value) || ''
    };
  }

  /* Detail: full data including variants, prices, metafields. */
  function normaliseDetail(p) {
    var images = ((p.images && p.images.edges) || []).map(function (e) {
      return e.node.url;
    });
    var leadImg = (p.featuredImage && p.featuredImage.url) || images[0] || '';

    var variants = ((p.variants && p.variants.edges) || []).map(function (e) {
      var v = e.node;
      var optsMap = {};
      (v.selectedOptions || []).forEach(function (o) {
        optsMap[o.name.toLowerCase()] = o.value;
      });
      return {
        id:    v.id,
        title: v.title,
        avail: !!v.availableForSale,
        price: v.price ? parseFloat(v.price.amount) : 0,
        opts:  optsMap,
        img:   (v.image && v.image.url) || ''
      };
    });

    var embellishment = safeJson(p.embellishmentMf && p.embellishmentMf.value) || [];
    var priceTiersHg  = safeJson(p.priceTiersMf  && p.priceTiersMf.value);
    var decoOptions   = safeJson(p.decoOptionsMf && p.decoOptionsMf.value);
    var availSizes    = safeJson(p.availableSizesMf && p.availableSizesMf.value) || [];

    /* Apparel tier prices: variants have option2 in {"1-9","10-49","50+"}
       — pluck a representative price for each tier. */
    var apTierPrices = {};
    variants.forEach(function (v) {
      var tier = v.opts['option2'] || v.opts['qty tier'] || v.opts['tier'];
      /* Storefront returns option names by their actual product option name
         (e.g. "Size", "Tier"); fall back by looking through values. */
      if (!tier) {
        Object.keys(v.opts).forEach(function (k) {
          var val = v.opts[k];
          if (val === '1-9' || val === '10-49' || val === '50+') tier = val;
        });
      }
      if (tier && apTierPrices[tier] == null && v.price) {
        apTierPrices[tier] = v.price;
      }
    });

    /* Sizes: prefer the available_sizes metafield, fall back to variant
       option1 values (deduped). */
    var sizes = availSizes.length
      ? availSizes
      : (function () {
          var seen = {}, list = [];
          variants.forEach(function (v) {
            var s = v.opts['size'] || v.opts['option1'];
            if (!s) {
              /* Pick the first non-tier option value */
              Object.keys(v.opts).forEach(function (k) {
                var val = v.opts[k];
                if (val !== '1-9' && val !== '10-49' && val !== '50+' && !seen[val]) {
                  s = val;
                }
              });
            }
            if (s && !seen[s] && s.toLowerCase() !== 'default title') {
              seen[s] = true; list.push(s);
            }
          });
          return list;
        })();

    var isApparel   = embellishment.length > 0 && !decoOptions;
    var isHardGoods = !!decoOptions;

    var colourFromMf = (p.colourMf && p.colourMf.value) || '';

    return {
      i:   p.id,
      t:   p.title,
      v:   p.vendor,
      y:   p.productType,
      img: leadImg,
      cw:  colourFromMf ? [colourFromMf] : [],   /* sibling resolve will fold in others */
      ci:  colourFromMf && leadImg ? (function () { var o = {}; o[colourFromMf] = leadImg; return o; })() : {},
      sz:  sizes,
      h:   p.handle,
      em:  embellishment,
      ap:  isApparel,
      hg:  isHardGoods,
      ap_p: Object.keys(apTierPrices).length ? apTierPrices : null,
      hg_p: priceTiersHg,
      'do': decoOptions,
      moq: safeInt(p.moqMf && p.moqMf.value),
      fw:  (p.fabricWeightMf && p.fabricWeightMf.value) || '',
      vm:  safeInt(p.volumeMlMf && p.volumeMlMf.value),
      _variants: variants,
      _availableForSale: !!p.availableForSale,
      _siblingsCollectionId: (p.siblingsRef && p.siblingsRef.reference && p.siblingsRef.reference.id)
                             || (p.siblingsRef && p.siblingsRef.value) || null
    };
  }


  /* ════════════════════════════════════════════════════════════════════
     STYLE GROUPING — merge a product's siblings into a single style entry
     ════════════════════════════════════════════════════════════════════ */

  /**
   * Given a single product handle, return the merged style entry — the
   * lead colourway folded together with all siblings. Used when adding
   * a line: we don't want one line per colourway, we want one line that
   * lets the buyer pick any colour.
   */
  function getStyleByHandle(handle) {
    return resolveSiblings(handle).then(function (members) {
      if (!members || members.length === 0) return null;

      /* Sort: prefer Black > Navy > White > rest, alpha within rest. */
      var rank = function (c) {
        var n = (c || '').toLowerCase();
        if (n === 'black') return 0;
        if (n === 'navy')  return 1;
        if (n === 'white') return 2;
        return 50;
      };
      var sorted = members.slice().sort(function (a, b) {
        var ra = rank(a.cw[0]); var rb = rank(b.cw[0]);
        if (ra !== rb) return ra - rb;
        return (a.cw[0] || '').localeCompare(b.cw[0] || '');
      });
      var lead = sorted[0];

      /* Merge colour list and per-colour images from all members */
      var cw = [];
      var ci = {};
      sorted.forEach(function (m) {
        (m.cw || []).forEach(function (c) {
          if (cw.indexOf(c) === -1) cw.push(c);
          if (m.ci && m.ci[c]) ci[c] = m.ci[c];
        });
      });

      /* Output mirrors the catalogue.json shape */
      return {
        i:    lead._siblingsCollectionId || ('solo:' + lead.h),
        t:    cleanTitle(lead.t, lead.cw[0]),
        v:    lead.v,
        y:    lead.y,
        img:  lead.img,
        cw:   cw,
        ci:   ci,
        sz:   lead.sz,
        h:    lead.h,
        em:   lead.em,
        ap:   lead.ap,
        hg:   lead.hg,
        ap_p: lead.ap_p,
        hg_p: lead.hg_p,
        'do': lead['do'],
        moq:  lead.moq,
        fw:   lead.fw,
        vm:   lead.vm,
        /* Storefront-only extras */
        _members:   sorted,
        _variants:  lead._variants
      };
    });
  }

  /**
   * Strip "(Colour)" suffix from a product title for cleaner display.
   * Mirrors the logic from build-catalogue.py.
   */
  function cleanTitle(t, colour) {
    if (!t) return '';
    var clean = String(t).trim();
    if (colour && clean.endsWith('(' + colour + ')')) {
      clean = clean.slice(0, -(colour.length + 2)).trim();
    }
    if (clean.endsWith(')')) {
      var open = clean.lastIndexOf('(');
      if (open > 0) {
        var inner = clean.slice(open + 1, -1);
        if (inner.length <= 30 && /^[A-Za-z &]+$/.test(inner)) {
          clean = clean.slice(0, open).trim();
        }
      }
    }
    return clean.replace(/\s*\|\s*/g, ' ').replace(/\s+/g, ' ').trim();
  }


  /* ════════════════════════════════════════════════════════════════════
     PUBLIC API
     ════════════════════════════════════════════════════════════════════ */
  var api = {
    init:             init,
    searchProducts:   searchProducts,
    listProducts:     listProducts,
    getProduct:       getProduct,
    getStyleByHandle: getStyleByHandle,
    resolveSiblings:  resolveSiblings,
    listFilters:      listFilters,

    /* Diagnostics */
    _cache: { products: ProductCache, search: SearchCache, siblings: SiblingsCache },
    _config: CONFIG
  };

  if (typeof window !== 'undefined') root.pwStorefront = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

})(typeof window !== 'undefined' ? window : globalThis);
